// AutoCompleteInput.js
import React, { useCallback, useEffect, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import useDebounce from "../hooks/useDebounce";
import baseService from "../services/baseService";

const AutoCompleteInput = ({
  entity,
  column = "name",
  columnLabel = "name",
  columnDetails,
  onChange = () => {},
  placeholder = "Digite para buscar...",
  isMulti = false,
  isClearable = false,
  value,
  filters = {},
  exclude_ids = [],
  disabled = false,
  callBack = () => {},
  externalOptions = null,
  label = "",
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [internalSelectedValue, setInternalSelectedValue] = useState(
    value || (isMulti ? [] : null)
  );

  const fetchOptions = useCallback(
    async (searchTerm) => {
      if (externalOptions) {
        const filtered = externalOptions.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setOptions(filtered);
        return;
      }

      setLoading(true);
      try {
        const params = {
          page: 1,
          perPage: 15,
          exclude_ids,
          ...filters,
        };

        if (searchTerm?.trim()) {
          params[column] = searchTerm;
        }

        const response = await baseService.autocomplete(entity, params);
        const data = response?.result?.data || [];

        const mapped = data.map((item) => ({
          value: item.id,
          label:
            item[columnLabel] +
            (columnDetails && item[columnDetails]
              ? ` - ${item[columnDetails]}`
              : ""),
        }));

        setOptions(mapped);
      } catch (error) {
        console.error("Erro ao buscar opções:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [
      externalOptions,
      filters,
      entity,
      column,
      columnLabel,
      columnDetails,
      exclude_ids,
    ]
  );

  const { debouncedFn } = useDebounce(fetchOptions, 500);

  useEffect(() => {
    setInternalSelectedValue(value || (isMulti ? [] : null));
  }, [value, isMulti]);

  useEffect(() => {
    if (externalOptions) {
      callBack(() => {});
    } else {
      const refetch = () => fetchOptions(inputValue);
      callBack(refetch);
    }

    return () => callBack(null);
  }, [externalOptions, callBack, fetchOptions, inputValue]);

  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);
    if (reason === "input") {
      debouncedFn(newInputValue);
    }
  };

  const handleChange = (event, newValue) => {
    setInternalSelectedValue(newValue);
    onChange(newValue);
  };

  return (
    <Autocomplete
      multiple={isMulti}
      disabled={disabled}
      options={options ?? []}
      value={internalSelectedValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => {
        if (option && typeof option === "object" && "label" in option) {
          return String(option.label);
        }
        return "";
      }}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      disableClearable={!isClearable}
      clearOnBlur={false}
      disableCloseOnSelect={isMulti}
      noOptionsText="Nenhuma opção encontrada"
      loading={loading}
      disablePortal
      fullWidth
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          sx={{
            // Estilo do input desabilitado
            "& .Mui-disabled": {
              backgroundColor: "#EBEBEB",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#DEDEDE !important",
                borderWidth: "1px !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#DEDEDE !important",
              },
              "& .MuiInputBase-input": {
                color: "#616161", // Cor do texto quando há dados
                "-webkit-text-fill-color": "#616161",
                "&::placeholder": {
                  color: "#9e9e9e",
                  opacity: 1,
                  fontWeight: "normal",
                  "-webkit-text-fill-color": "#9e9e9e", // Para Safari
                },
              },
              "& .MuiInputAdornment-root": {
                color: "#A0A0A0",
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AutoCompleteInput;
